import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  LinearProgress,
} from '@mui/material';
import { Close, DeleteOutline, EditOutlined, Restore } from '@mui/icons-material';
import { getFaceImage } from '../../utils/api';

const PRIMARY = '#40518A';

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const qualityChipSx = (color) => ({
  height: 24,
  borderRadius: '6px',
  fontWeight: 700,
  bgcolor: '#fff',
  color,
  border: '1px solid',
  borderColor: color,
});

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  px: 1.5,
  minHeight: 34,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const DetailRow = ({ label, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '108px minmax(0, 1fr)',
      alignItems: 'center',
      gap: 1.5,
      py: 1.05,
      borderBottom: '1px solid #eef1f6',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="caption" sx={{ color: '#64748b', letterSpacing: 0.4, fontWeight: 700, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const EmployeeFacesDialog = ({
  open,
  person,
  onClose,
  formatAngleLabel,
  getQualityColor,
  formatDate,
  onFaceImageClick,
  showDeleted = false,
  updatedBy = '',
  onEdit,
  onDeactivate,
  onRestore,
}) => {
  const faces = useMemo(() => {
    const allFaces = Array.isArray(person?.faces) ? person.faces : [];

    return showDeleted
      ? allFaces.filter((face) => !!face.deleted_at)
      : allFaces.filter((face) => !face.deleted_at);
  }, [person?.faces, showDeleted]);

  const [images, setImages] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const preferred = faces.find((face) => face.face_angle === 'front' && face.face_id) || faces[0];
    setSelectedId((current) => (
      current && faces.some((face) => face.face_id === current) ? current : (preferred?.face_id || null)
    ));
    if (faces.length === 0) {
      setSelectedId(null);
      setImages({});
      setLoadingImages(false);
      return undefined;
    }

    // Keep current selection if it is still active.
    // Otherwise select Front, then the next available face.
    setSelectedId((previousId) => {
      const currentFace = faces.find(
        (face) => String(face.face_id) === String(previousId)
      );

      if (currentFace) return currentFace.face_id;

      const preferred =
        faces.find(
          (face) =>
            String(face.face_angle || '').toLowerCase() === 'front'
        ) || faces[0];

      return preferred?.face_id || null;
    });

    setLoadingImages(true);

    (async () => {
      const next = {};

      await Promise.all(
        faces.map(async (face) => {
          if (!face?.face_id) return;

          try {
            const blob = await getFaceImage(face.face_id);
            next[face.face_id] = await blobToDataUrl(blob);
          } catch (err) {
            console.error('Failed to load face angle image:', err);
            next[face.face_id] = null;
          }
        })
      );

      if (!cancelled) {
        setImages(next);
        setLoadingImages(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, faces]);

  const selected = faces.find((face) => face.face_id === selectedId) || faces[0] || null;
  const selectedImage = selected ? images[selected.face_id] : null;
  const angleLabel = selected
    ? (formatAngleLabel(selected.face_angle, selected.metadata) || 'Angle')
    : '';

  const faceData = selected ? {
    face_id: selected.face_id,
    face_angle: selected.face_angle,
    pose_quality: selected.pose_quality,
    lighting_quality: selected.lighting_quality,
    created_at: selected.created_at,
    last_updated: selected.last_updated,
    deleted_at: selected.deleted_at,
    metadata: selected.metadata || {},
    name: person?.name,
    person_id: person?.person_id,
  } : null;

  const updatedByLabel = updatedBy && updatedBy !== '-' ? updatedBy : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#fff',
          width: '100%',
          maxWidth: 1120,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #eef1f6',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2, color: '#0f172a' }}>
            {person?.name || 'Faces'}
          </Typography>
          {(person?.employee_code || person?.person_id) ? (
            <Typography variant="body2" noWrap sx={{ color: '#64748b', mt: 0.25 }}>
              {person.employee_code || person.person_id}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="Close faces" onClick={onClose} size="small" sx={{ color: '#475569' }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2, bgcolor: '#f8fafc' }}>
        {faces.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No face angles enrolled.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 0.85fr) minmax(460px, 1.15fr)' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 3,
                  bgcolor: '#eef2f7',
                  minHeight: 180,
                  p: 2,
                  boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)',
                }}
              >
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt={angleLabel}
                    onClick={() => {
                      if (selected && onFaceImageClick) {
                        onFaceImageClick(selected);
                      }
                    }}
                    sx={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: { xs: 240, md: 320 },
                      objectFit: 'contain',
                      borderRadius: 2,
                      cursor: 'zoom-in',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 160,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {loadingImages ? (
                      <CircularProgress size={28} sx={{ color: PRIMARY }} />
                    ) : (
                      <Typography color="text.secondary">
                        Image unavailable
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  overflowX: 'auto',
                  justifyContent: faces.length <= 4 ? 'center' : 'flex-start',
                  pb: 0.25,
                }}
              >
                {faces.map((face) => {
                  const label = formatAngleLabel(face.face_angle, face.metadata) || 'Angle';
                  const active = face.face_id === selected?.face_id;
                  const thumb = images[face.face_id];
                  return (
                    <Box
                      key={face.face_id}
                      component="button"
                      type="button"
                      aria-pressed={active}
                      aria-label={`Show ${label}`}
                      onClick={() => {
                        setSelectedId(face.face_id);
                      }}
                      sx={{
                        border: '2px solid',
                        borderColor: active ? PRIMARY : '#e2e8f0',
                        bgcolor: '#fff',
                        p: 0.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        flex: '0 0 auto',
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        boxShadow: active ? '0 8px 18px rgba(64, 81, 138, 0.18)' : 'none',
                        '&:hover': { borderColor: active ? PRIMARY : '#cbd5e1' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1.25,
                          overflow: 'hidden',
                          bgcolor: '#eef2f7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt={label}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (onFaceImageClick) {
                                onFaceImageClick(face);
                              }
                            }}
                            sx={{
                              display: 'block',
                              width: 'auto',
                              height: 'auto',
                              maxWidth: '100%',
                              maxHeight: { xs: 240, md: 320 },
                              objectFit: 'contain',
                              borderRadius: 2,
                              cursor: 'zoom-in',
                            }}
                          />) : loadingImages ? (
                            <CircularProgress size={16} sx={{ color: PRIMARY }} />
                          ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          display: 'block',
                          textAlign: 'center',
                          mt: 0.4,
                          fontWeight: active ? 700 : 500,
                          textTransform: 'capitalize',
                          color: active ? PRIMARY : '#64748b',
                          maxWidth: 64,
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>

            {faceData ? (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2.5,
                  border: '1px solid #e8edf5',
                  px: 2,
                  py: 0.5,
                }}
              >
                <DetailRow label="Angle">
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', color: '#0f172a' }}>{angleLabel}</Typography>
                </DetailRow>
                <DetailRow label="Pose">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={(faceData.pose_quality || 0) * 100}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 4,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getQualityColor(faceData.pose_quality),
                          borderRadius: 4,
                        },
                      }}
                    />

                    <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
                      {((faceData.pose_quality || 0) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </DetailRow>
                <DetailRow label="Lighting">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={(faceData.lighting_quality || 0) * 100}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 4,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getQualityColor(faceData.lighting_quality),
                          borderRadius: 4,
                        },
                      }}
                    />

                    <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
                      {((faceData.lighting_quality || 0) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </DetailRow>
                <DetailRow label="Created">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{formatDate(faceData.created_at)}</Typography>
                </DetailRow>
                <DetailRow label="Updated">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{formatDate(faceData.last_updated)}</Typography>
                </DetailRow>
                <DetailRow label="Updated by">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{updatedByLabel}</Typography>
                </DetailRow>
                {showDeleted ? (
                  <DetailRow label="Deleted">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{formatDate(faceData.deleted_at)}</Typography>
                  </DetailRow>
                ) : null}
                <DetailRow label="Actions">
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap' }}>
                    {!showDeleted && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditOutlined sx={{ fontSize: 18 }} />}
                        onClick={() => onEdit && onEdit(faceData)}
                        sx={{
                          ...actionButtonSx,
                          color: PRIMARY,
                          borderColor: '#c5cee0',
                          '&:hover': { borderColor: PRIMARY, bgcolor: 'rgba(64, 81, 138, 0.06)' },
                        }}
                      >
                        Edit image
                      </Button>
                    )}
                    {!showDeleted ? (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline sx={{ fontSize: 18 }} />}
                        onClick={() => onDeactivate && onDeactivate(faceData)}
                        sx={actionButtonSx}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={<Restore sx={{ fontSize: 18 }} />}
                        onClick={() => onRestore && onRestore(faceData)}
                        sx={actionButtonSx}
                      >
                        Activate
                      </Button>
                    )}
                  </Stack>
                </DetailRow>
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: '1px solid #eef1f6' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: PRIMARY }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeFacesDialog;
